import { useState } from 'react';
import { Box, Group, Stack, Switch } from '@mantine/core';
import { ROKU_PREVIEW_GEOMETRY as G } from '@/lib/constants/appFactory';
import { Missing, Preview, TvFrame } from './TvDevicePreviews';

interface RokuScreenPreviewsProps {
  /** name → URL of the set's files (BrandKitUtils.previewFileMap). */
  files: Record<string, string>;
  /** Width of each TV frame in the preview, px (the frame is 16:9). */
  frameWidth?: number;
}

const PLACEHOLDER = '#2a2f3a';
const MENU = '#1c1f26';

/**
 * Roku parity (jvm notes/ott/tv-graphics/design_roku_parity_20260928.md R6):
 * the Roku set drawn where Roku shows it — the channel poster in the home
 * screen's grid of installed apps (approximate on-screen size: Roku
 * publishes the 540 x 405 art, not the tile), the Channel Store poster, and
 * the full-screen splash. Pure CSS, like TvDevicePreviews.
 */
export function RokuScreenPreviews({ files, frameWidth = 560 }: RokuScreenPreviewsProps) {
  const [focused, setFocused] = useState(true);
  const k = frameWidth / G.frameW;
  const tile = { w: G.tilePx.w * k, h: G.tilePx.h * k };
  const gap = G.gapPx * k;

  return (
    <Stack gap="lg">
      <Preview title="Roku — Home screen" note="Your channel poster in the grid of installed apps (approximate size).">
        <TvFrame width={frameWidth}>
          <Group gap={gap} wrap="nowrap" align="center" style={{ width: '100%', height: '100%', padding: gap }}>
            <Box style={{ width: G.menuW * k, height: '100%', background: MENU, borderRadius: 4, flex: 'none' }} />
            <Stack gap={gap}>
              {[0, 1].map((row) => (
                <Group key={row} gap={gap} wrap="nowrap">
                  {[0, 1, 2].map((col) =>
                    row === 0 && col === 0 ? (
                      <Poster key={col} w={tile.w} h={tile.h} src={files['icon_fhd.png']} focused={focused} />
                    ) : (
                      <Poster key={col} w={tile.w} h={tile.h} />
                    ),
                  )}
                </Group>
              ))}
            </Stack>
          </Group>
        </TvFrame>
        <Switch size="xs" mt={6} label="Focused" checked={focused} onChange={(e) => setFocused(e.currentTarget.checked)} />
      </Preview>

      <Preview title="Roku Channel Store" note="The poster on your store page.">
        <Poster w={270} h={202} src={files['store_poster.png']} alt="Store poster" />
      </Preview>

      <Preview title="Splash" note="What a viewer sees while the app opens.">
        <TvFrame width={frameWidth} ground={files['splash_fhd.jpg']}>
          {!files['splash_fhd.jpg'] && <Missing />}
        </TvFrame>
      </Preview>
    </Stack>
  );
}

function Poster({ w, h, src, alt = 'Roku channel poster', focused }: { w: number; h: number; src?: string; alt?: string; focused?: boolean }) {
  return (
    <Box
      data-testid={src ? 'roku-poster' : undefined}
      style={{
        width: w,
        height: h,
        flex: 'none',
        borderRadius: 6,
        overflow: 'hidden',
        background: PLACEHOLDER,
        transform: focused ? 'scale(1.06)' : undefined,
        boxShadow: focused ? '0 0 0 3px #ffffff' : undefined,
      }}
    >
      {src ? <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
    </Box>
  );
}
